import { ulid } from 'ulid';
import pkg from '../../../package.json';
import { UIMessage } from 'ai';

/** Current application version taken from package.json */
export const APP_VERSION: string = (pkg as { version: string }).version;

/**
 * A single chat message exchanged between the author (user) and the AI assistant.
 */
export interface ChatMessage extends UIMessage {
  metadata?: {
    createdAt?: string;
    traceId?: string;
    /**
     * Screen state captured before each edit was applied.
     * Keyed by editIndex → screen YAML string.
     */
    checkpoints?: Record<number, string>;
  };
}

/**
 * A chat session is scoped to a single course (courseId + optional firmCode).
 * The session id is generated via {@link generateSessionId}.
 */
export interface ChatSession {
  /** `${courseId}_${firmCode ?? 'NA'}_${ulid()}` */
  id: string;
  courseId: number;
  firmCode?: string;
  /** First user message (truncated) – used as the UI title. */
  title: string;
  createdAt: number;
  updatedAt: number;
  /** Version of the application when the chat was last updated */
  version: string;
  messages: ChatMessage[];

  /**
   * Patch acceptance / rejection bookkeeping keyed by `${messageId}_${editIndex}` → 'accepted' | 'rejected'.
   * This allows the UI to remember which patches have been actioned even after reload.
   */
  patchStates?: Record<string, 'accepted' | 'rejected'>;
}

const DB_NAME = 'ubercourse-chat';
const DB_VERSION = 1;
const STORE_NAME = 'chats';

/**
 * Lightweight wrapper around the browser IndexedDB API for persisting chat
 * sessions locally. It purposefully avoids external helpers (idb, Dexie, etc.)
 * to keep the dependency surface minimal.
 */
export class ChatStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  /** Lazily open (or create) the IndexedDB database */
  private getDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });

    return this.dbPromise;
  }

  /** Ensure DB is initialised (mainly for tests) */
  async init(): Promise<void> {
    await this.getDb();
  }

  /**
   * Persist or update a chat session by its id.
   * The key is prefixed with `uc-chat-` to allow future namespacing.
   */
  async saveSession(session: ChatSession): Promise<void> {
    const db = await this.getDb();

    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(JSON.parse(JSON.stringify(session)), this.makeKey(session.id));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Load a chat session by id or `null` if not found */
  async loadSession(id: string): Promise<ChatSession | null> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(this.makeKey(id));
      req.onsuccess = () => {
        resolve((req.result as ChatSession) ?? null);
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Return all sessions for the given course/firm, sorted by `updatedAt` DESC.
   */
  async listSessions(
    courseId: number,
    firmCode?: string
  ): Promise<ChatSession[]> {
    const db = await this.getDb();

    return new Promise((resolve, reject) => {
      const sessions: ChatSession[] = [];
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.openCursor();

      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cursor = req.result as IDBCursorWithValue | null;
        if (cursor) {
          const value = cursor.value as ChatSession;
          if (value.courseId === courseId && value.firmCode === firmCode) {
            sessions.push(value);
          }
          cursor.continue();
        } else {
          // All rows read – sort by updatedAt DESC
          sessions.sort((a, b) => b.updatedAt - a.updatedAt);
          resolve(sessions);
        }
      };
    });
  }

  /** Remove a chat session entirely */
  async deleteSession(id: string): Promise<void> {
    const db = await this.getDb();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(this.makeKey(id));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */

  private makeKey(id: string) {
    return `uc-chat-${id}`;
  }
}

/** Singleton instance used across the app */
export const chatStore = new ChatStore();

/* ------------------------------------------------------------------ */
/* Top-level helper functions (thin wrappers around ChatStore)         */
/* ------------------------------------------------------------------ */

export async function saveChat(session: ChatSession): Promise<void> {
  // Ensure the session always carries the latest application version
  session.version = APP_VERSION;
  await chatStore.saveSession(session);
}

export async function loadChat(id: string): Promise<ChatSession | null> {
  return chatStore.loadSession(id);
}

export async function listChats(
  courseId: number,
  firmCode?: string
): Promise<ChatSession[]> {
  return chatStore.listSessions(courseId, firmCode);
}

export async function deleteChat(id: string): Promise<void> {
  await chatStore.deleteSession(id);
}

/**
 * Convenience utility – generates a stable unique session id.
 */
export function generateSessionId(courseId: number, firmCode?: string): string {
  return `${courseId}${firmCode ? `_${firmCode}` : ''}_${ulid()}`;
}
